import React, { useState } from "react";
import { Modal, Button, Label, TextInput } from "flowbite-react";
import { WorkspaceTag } from "./WorkspaceTag";
import { HiPlus } from "react-icons/hi";
import { WorkspaceTagType } from "../../pages/workspace/Api";
import { YMButton } from "../../ym-components";

interface TagManagerProps {
  show: boolean;
  onClose: () => void;
  existingTags: WorkspaceTagType[];
  selectedTags: WorkspaceTagType[];
  onCreateTag: (name: string, color: string) => Promise<string>;
  onSaveTags: (tagIds: string[]) => Promise<void>;
}

const colors = ["indigo", "blue", "red", "amber", "emerald", "gray"];

export function TagManager({
  show,
  onClose,
  existingTags,
  selectedTags,
  onCreateTag,
  onSaveTags,
}: TagManagerProps) {
  const [newTagName, setNewTagName] = useState("");
  const [selectedColor, setSelectedColor] = useState("indigo");
  const [currentTags, setCurrentTags] = useState<WorkspaceTagType[]>(selectedTags);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  function handleCreateTag() {
    if (!newTagName.trim()) {
      return;
    }

    setIsCreating(true);
    onCreateTag(newTagName, selectedColor)
      .then((tagId) => {
        const newTag: WorkspaceTagType = {
          name: newTagName,
          color: selectedColor,
        };
        setCurrentTags([...currentTags, newTag]);
        setNewTagName("");
        setSelectedColor("indigo");
      })
      .catch((error) => {
        console.error("Failed to create tag:", error);
      })
      .finally(() => {
        setIsCreating(false);
      });
  }

  function handleAddExistingTag(tag: WorkspaceTagType) {
    if (!currentTags.some((t) => t.name === tag.name)) {
      setCurrentTags([...currentTags, tag]);
    }
  }

  function handleRemoveTag(tagName: string) {
    setCurrentTags(currentTags.filter((t) => t.name !== tagName));
  }

  function handleSave() {
    setIsSaving(true);
    // For now, we'll just pass tag names since the backend might not have IDs yet
    // This might need adjustment based on your actual API
    onSaveTags(currentTags.map((t) => t.name))
      .then(() => {
        onClose();
      })
      .catch((error) => {
        console.error("Failed to save tags:", error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }

  const availableTags = existingTags.filter(
    (tag) => !currentTags.some((t) => t.name === tag.name)
  );

  return (
    <Modal show={show} onClose={onClose} size="md">
      <Modal.Header>Gérer les tags</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Create new tag form */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Créer un nouveau tag
            </h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="tagName" value="Nom du tag" />
                <TextInput
                  id="tagName"
                  type="text"
                  placeholder="Entrez le nom du tag"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleCreateTag();
                    }
                  }}
                />
              </div>
              <div>
                <Label value="Couleur" />
                <div className="flex gap-2 mt-2">
                  {colors.map((c) => (
                    <div
                      key={c}
                      onClick={() => setSelectedColor(c)}
                      className={`h-6 w-6 cursor-pointer rounded-full bg-${c}-500 hover:bg-${c}-800 ring-2 ${
                        selectedColor === c ? "ring-gray-900" : "ring-gray-200"
                      }`}
                    />
                  ))}
                </div>
              </div>
              <Button
                size="xs"
                color="indigo"
                onClick={handleCreateTag}
                disabled={isCreating || !newTagName.trim()}
              >
                <HiPlus className="mr-2 h-4 w-4" />
                Créer le tag
              </Button>
            </div>
          </div>

          {/* Add existing tags */}
          {availableTags.length > 0 && (
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Ajouter des tags existants
              </h3>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <div
                    key={tag.name}
                    className="cursor-pointer"
                    onClick={() => handleAddExistingTag(tag)}
                  >
                    <WorkspaceTag tag={tag} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current tags */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Tags actuels
            </h3>
            {currentTags.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun tag sélectionné</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {currentTags.map((tag) => (
                  <WorkspaceTag
                    key={tag.name}
                    tag={tag}
                    showDelete
                    onDelete={() => handleRemoveTag(tag.name)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <YMButton
          text={isSaving ? "Sauvergarde..." : "Sauvegarder"}
          onClick={handleSave}
          disabled={isSaving}
        />
        <YMButton
          text={"Annuler"}
          onClick={onClose}
          color={"white"}
        />
      </Modal.Footer>
    </Modal>
  );
}
